/**
 * useCast — thin wrapper around Google's Cast Sender Framework SDK.
 *
 * What this does:
 *   • Lazy-loads `cast_sender.js` from gstatic exactly once for the
 *     whole app (the SDK installs itself on `window.cast` /
 *     `window.chrome.cast` and is shared between every component).
 *   • Initialises the CastContext with the DEFAULT MEDIA RECEIVER app
 *     ID (`CC1AD845`) — Google's built-in receiver that can play any
 *     HTTPS MP4/WebM/HLS without us having to publish our own
 *     receiver to the Cast SDK Developer Console.
 *   • Exposes a simple `{ available, casting, cast(...), stop() }`
 *     API so player components can drop in a Cast button with one
 *     hook call.
 *
 * Caveats (deliberate, not bugs):
 *   • YouTube iframe content cannot be Cast through this path — it's
 *     cross-origin sandboxed. Users can still tap "Cast..." in Chrome's
 *     own 3-dot menu to mirror the whole tab. We hide our button when
 *     no native MP4/HLS source is provided.
 *   • iOS Safari does NOT load the Sender SDK (`chrome.cast` is
 *     Chrome-only). On iOS the AirPlay button in CastButton.jsx is the
 *     parallel control. This hook will simply report `available:false`
 *     on iOS, which is the right answer.
 */
import { useEffect, useState } from "react";

const CAST_SDK_SRC =
  "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";

// Module-level singletons — only ever one SDK load and one context.
let sdkPromise = null;
let contextInitialised = false;

function loadSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    // Cast SDK signals readiness via window.__onGCastApiAvailable.
    // We chain any prior callback so we don't stomp on it.
    const prior = window.__onGCastApiAvailable;
    window.__onGCastApiAvailable = (isAvailable) => {
      try { if (typeof prior === "function") prior(isAvailable); } catch (_) {}
      if (isAvailable) {
        try {
          if (!contextInitialised && window.cast && window.cast.framework) {
            window.cast.framework.CastContext.getInstance().setOptions({
              receiverApplicationId:
                window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
              autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            });
            contextInitialised = true;
          }
          resolve(true);
        } catch (e) {
          reject(e);
        }
      } else {
        reject(new Error("Cast SDK unavailable"));
      }
    };
    const s = document.createElement("script");
    s.src = CAST_SDK_SRC;
    s.async = true;
    s.onerror = () => reject(new Error("Cast SDK script failed to load"));
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export default function useCast() {
  const [available, setAvailable] = useState(false);
  const [casting, setCasting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // The Cast Sender SDK is Chrome/Edge-only. Don't even try to load
    // it on Safari / Firefox / iOS — it just adds noise to the console.
    const ua = navigator.userAgent || "";
    const isChromium = /Chrome|CriOS|Edg|Chromium/i.test(ua) && !/Firefox/i.test(ua);
    if (!isChromium) return;
    let cancelled = false;
    const cleanupRef = { fn: () => {} };
    loadSdk()
      .then(() => {
        if (cancelled) return;
        const ctx = window.cast.framework.CastContext.getInstance();
        const onCastState = () => {
          if (cancelled) return;
          const state = ctx.getCastState();
          // NO_DEVICES_AVAILABLE | NOT_CONNECTED | CONNECTING | CONNECTED
          setAvailable(state !== window.cast.framework.CastState.NO_DEVICES_AVAILABLE);
          setCasting(state === window.cast.framework.CastState.CONNECTED);
        };
        onCastState();
        ctx.addEventListener(
          window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          onCastState
        );
        // Pin the listener for cleanup.
        cleanupRef.fn = () => {
          try {
            ctx.removeEventListener(
              window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
              onCastState
            );
          } catch (_) {}
        };
      })
      .catch(() => { /* swallow — Cast just won't show */ });
    return () => { cancelled = true; try { cleanupRef.fn(); } catch (_) {} };
  }, []);

  // Cast a single media item. `metadata` is optional and surfaces on
  // the TV screen (title + subtitle + poster). Returns a Promise so
  // callers can show a toast on rejection.
  const cast = async ({ url, contentType = "video/mp4", title, subtitle, poster }) => {
    if (!url) throw new Error("No URL");
    setLoading(true);
    try {
      const ctx = window.cast.framework.CastContext.getInstance();
      // requestSession opens the device picker UI if not already connected.
      await ctx.requestSession();
      const session = ctx.getCurrentSession();
      if (!session) throw new Error("No Cast session");
      const mediaInfo = new window.chrome.cast.media.MediaInfo(url, contentType);
      mediaInfo.metadata = new window.chrome.cast.media.GenericMediaMetadata();
      if (title) mediaInfo.metadata.title = title;
      if (subtitle) mediaInfo.metadata.subtitle = subtitle;
      if (poster) mediaInfo.metadata.images = [new window.chrome.cast.Image(poster)];
      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
      await session.loadMedia(request);
      setCasting(true);
    } finally {
      setLoading(false);
    }
  };

  const stop = async () => {
    try {
      const session = window.cast.framework.CastContext.getInstance().getCurrentSession();
      if (session) await session.endSession(true);
      setCasting(false);
    } catch (_) {}
  };

  return { available, casting, loading, cast, stop };
}
